'use client';

import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';
import { IDynamicsOptionSet } from '@/types/dynamicsForms';
import { IComponent } from '@lamesarv-sdk/types';
import { Fragment, RefObject, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../Modal/Modal';
import { GenericHtmlComponents } from './GenericHtmlComponents';
import { GenericHtmlForms } from './GenericHtmlForms';

const getHashSanitized = (hash?: string) => {
  if (!hash || hash?.trim() === '#') return undefined;
  return hash.replaceAll('#', '').trim().toLowerCase();
};

const removeHash = () => {
  if (typeof window === 'undefined') return;
  if ('replaceState' in window.history) {
    window.history.replaceState('', window.document.title, window.location.pathname + window.location.search);
  }
};

interface IHtmlScript {
  attrs: Record<string, string>;
  code: string;
}

const SCRIPT_REGEX = /<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi;
const ATTR_REGEX = /([^\s=/]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;

/**
 * Splits <script> tags out of the CMS HTML. Scripts left inside the server HTML
 * are executed by the browser while it parses the page, i.e. BEFORE React
 * hydrates, so loaders such as the Dynamics FormLoader mutate the DOM early and
 * break hydration (React errors #418/#423). Pure string work so it behaves the
 * same on server and client.
 */
const splitScripts = (html: string): { html: string; scripts: IHtmlScript[] } => {
  const scripts: IHtmlScript[] = [];
  const clean = html.replaceAll(SCRIPT_REGEX, (_match, rawAttrs: string, code: string) => {
    const attrs: Record<string, string> = {};
    Array.from(rawAttrs.matchAll(ATTR_REGEX)).forEach((m) => {
      attrs[m[1]] = m[2] ?? m[3] ?? m[4] ?? '';
    });
    scripts.push({ attrs, code });
    return '';
  });
  return { html: clean, scripts };
};

// Content with scripts, forms or embed slots is dynamic: third parties and the
// browser's HTML parser (unclosed/invalid nesting) reshape it, so it is injected
// on the client only instead of being part of the hydrated tree.
const isDynamicContent = (html: string) => /<script\b|<form\b|data-embed=/i.test(html);

interface IStaticHtmlProps {
  html: string;
  className?: string;
  supportId?: string;
  containerRef: RefObject<HTMLDivElement>;
}

/**
 * Raw CMS HTML. Scripts are stripped from the markup and appended after mount so
 * they run post-hydration. Memoized so React never re-applies innerHTML (which
 * would wipe what those scripts render, e.g. Dynamics forms).
 */
const StaticHtml = memo(
  ({ html, className, supportId, containerRef }: IStaticHtmlProps) => {
    const { html: cleanHtml, scripts } = useMemo(() => splitScripts(html), [html]);

    const clientOnly = useMemo(() => isDynamicContent(html), [html]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      if (clientOnly) container.innerHTML = cleanHtml;
      if (scripts.length === 0) return;

      const added = scripts.map(({ attrs, code }) => {
        const el = document.createElement('script');
        for (const [name, value] of Object.entries(attrs)) el.setAttribute(name, value);
        if (code) el.text = code;
        container.appendChild(el);
        return el;
      });

      return () => added.forEach((el) => el.remove());
    }, [clientOnly, cleanHtml, scripts, containerRef]);

    return (
      <div
        ref={containerRef}
        suppressHydrationWarning
        dangerouslySetInnerHTML={clientOnly ? undefined : { __html: cleanHtml }}
        className={className}
        data-component-support-id={supportId}
      />
    );
  },
  (prev, next) => prev.html === next.html && prev.className === next.className && prev.supportId === next.supportId,
);
StaticHtml.displayName = 'StaticHtml';

// --

export interface IGenericHtmlModalProps {
  content: string;
  modal?: boolean;
  modal_url_parameter?: string;
  modal_hash_parameter?: string;
  // allow_scripts?: boolean;
  forms_support?: boolean;
  components_support_id?: string;
  embeddedComponents?: IComponent[];
  locationsOptionSet?: IDynamicsOptionSet;
  class?: string; // Legacy support
  classes?: string;
}

export const GenericHtmlModal = (props: IGenericHtmlModalProps) => {
  const { searchParams } = useCustomSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const hashParameter = getHashSanitized(props?.modal_hash_parameter);

  const onHashChanged = () => {
    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);

      if (hashParameter) {
        setModalOpen(hashLocation === hashParameter);
      }
    }
  };

  const onClose = () => {
    setModalOpen(false);
    if (hashParameter) {
      removeHash();
    }
  };

  useEffect(() => {
    let modalOpenStatus: boolean = !!props.modal;

    if (props.modal && props.modal_url_parameter) {
      if (!!searchParams.get(props.modal_url_parameter)) {
        modalOpenStatus = true;
      } else {
        modalOpenStatus = false;
      }
    }

    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);
      const hashParameter = getHashSanitized(props?.modal_hash_parameter);

      if (props.modal && !!hashParameter) {
        if (hashLocation === hashParameter) {
          modalOpenStatus = true;
        } else {
          modalOpenStatus = false;
        }
      }
    }

    setModalOpen(modalOpenStatus);

    // Hash Change Control
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', onHashChanged);
      return () => {
        window.removeEventListener('hashchange', onHashChanged);
      };
    }
  }, []);

  return (
    <Fragment>
      {props.modal ? (
        <Modal isOpen={modalOpen} onClose={onClose} useMaxWidth={false}>
          <StaticHtml
            containerRef={contentRef}
            html={props.content}
            className={props.class ?? props.classes}
            supportId={props.components_support_id}
          />
        </Modal>
      ) : (
        <StaticHtml
          containerRef={contentRef}
          html={props.content}
          className={props.class ?? props.classes}
          supportId={props.components_support_id}
        />
      )}
      {props.forms_support && <GenericHtmlForms containerRef={contentRef} />}
      {!!props.embeddedComponents?.length && (
        <GenericHtmlComponents
          containerRef={contentRef}
          components={props.embeddedComponents}
          locationsOptionSet={props.locationsOptionSet}
        />
      )}
    </Fragment>
  );
};
